import React, { Component, ErrorInfo, ReactNode } from 'react';
import ErrorFallback from './ErrorFallback';
import { reportError } from '../utils/errorReporting';

interface Props {
    children?: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

function getTopComponentName(componentStack?: string | null): string {
    const firstLine = componentStack?.trim().split('\n')[0]?.trim();
    return firstLine?.match(/^in (\S+)/)?.[1] ?? firstLine ?? 'unknown';
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        reportError(error, {
            componentStack: errorInfo.componentStack ?? undefined,
            tags: { component: getTopComponentName(errorInfo.componentStack) },
        });
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    public resetErrorBoundary = () => {
        this.setState({ hasError: false, error: null });
    };

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
            return <ErrorFallback error={this.state.error!} resetErrorBoundary={this.resetErrorBoundary} />;
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
